import { Formik } from "formik";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Col, Row, Spinner } from "react-bootstrap";
import ReactQuill from "react-quill-new";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import activeLoadStatusIcon from "../../../assets/images/Active_load.svg";
import inactiveStatusIcon from "../../../assets/images/Inactive_icon.svg";
import statusInStockIcon from "../../../assets/images/Status In Stock.svg";
import statusInstalledIcon from "../../../assets/images/Status Installed.svg";
import statusUnderRepairIcon from "../../../assets/images/Status Under Repair.svg";
import Breadcrumbs from "../../../components/Breadcrumbs";
import Button from "../../../components/button";
import CustomTabs from "../../../components/CustomTabs";
import FileUpload from "../../../components/FileUpload";
import Input from "../../../components/input";
import LogsComments from "../../../components/LogsComments";
import NewTableComponent from "../../../components/NewTableComponent";
import Select from "../../../components/Select";
import "../../../styles/Access.css";
import "../../../styles/AssetsRegistration.css";
import "../../../styles/Issue.css";
import "../../../styles/Services.css";
import "../../../styles/Trip.css";
import {
  clearTyreDetailState,
  clearTyreHistoryState,
  commentOnTyre,
  fetchTyreBrandsDropdown,
  fetchTyreDetail,
  fetchTyreHistory,
  fetchTyreModelsDropdown,
  fetchTyrePatternsDropdown,
  fetchTyreAssignmentHistory,
  fetchTyreServiceRecords,
  fetchTyreTrips,
  fetchTyreTypesDropdown,
  fetchTyresDropdown,
  updateTyreGeneralInfo,
} from "../../../store/Slices/TyreSlice";
import { getWarehousesDropdown } from "../../../store/Slices/PartVendorSlice";
import { fetchVehicleConstructionTypesDropdown } from "../../../store/Slices/VehicleDropdownSlice";
import {
  AppMainModules,
  AssetsPermission,
  AssetsSubModules,
} from "../../../utils/app-constant";
import {
  findCollectionByKeys,
  formats,
  getCurrentUser,
  hasPermission,
  modules,
  normalizeOptions,
} from "../../../utils/helper";
import { checkedIcon, closedIcon, inProgressIcon, openIcon, pendingIcon } from "../../../utils/svgs";
import { errorNotify, successNotify } from "../../../utils/toast";
import {
  constructionTypeOptions,
  tyreCurrentStatusOptions,
  tyrePatternOptions,
  tyreSizeOptions,
  tyreTypeOptions,
} from "./tyreAssets.constants";
import {
  depreciationMethodOptions,
  paymentTypeOptions,
} from "../Vehicles/vehicleAssets.constants";

const normalizeTyreStatusValue = (value = "") => {
  const normalizedValue = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

  if (normalizedValue === "installed_on_vehicle") {
    return "installed_vehicle";
  }

  if (normalizedValue === "installed_on_equipment") {
    return "installed_equipment";
  }

  if (normalizedValue === "installed") {
    return "installed";
  }

  return normalizedValue;
};

const resolveApiTyreStatus = (status) => {
  const normalizedStatus = normalizeTyreStatusValue(status);

  if (
    normalizedStatus === "installed_vehicle" ||
    normalizedStatus === "installed_equipment" ||
    normalizedStatus === "installed"
  ) {
    return "installed";
  }

  return normalizedStatus;
};

const normalizeAssetsRegistrationTab = (value, fallback = "tyres") => {
  const normalized = String(value || "").trim().toLowerCase();
  return ["vehicles", "equipments", "tyres"].includes(normalized)
    ? normalized
    : fallback;
};

const getAssetsRegistrationPath = (tab = "tyres") =>
  `/assets?tab=${normalizeAssetsRegistrationTab(tab)}`;

const getAssetsRegistrationReturnTab = (location, fallback = "tyres") => {
  const params = new URLSearchParams(location?.search || "");
  return normalizeAssetsRegistrationTab(
    params.get("tab") || location?.state?.activeTab || location?.state?.returnTab,
    fallback
  );
};

const stripHtml = (value) =>
  String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();

const toNumber = (value, fallback = 0) => {
  const parsed = Number(String(value ?? "").replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : fallback;
};

const formatAmount = (value) => toNumber(value, 0).toLocaleString("en-US");
const formatKm = (value) => `${toNumber(value, 0).toLocaleString("en-US")} km`;
const formatDecimal = (value) =>
  Number(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const normalizeComparableValue = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : "";
  }

  return String(value).trim();
};

const mapFiles = (files = []) =>
  (files || [])
    .map((file) => file?.fileName || file?.file_path || file?.path || file)
    .filter(Boolean);

const normalizeUploadedFiles = (files = []) => mapFiles(files);

const hasMeaningfulValue = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim() !== "";
  return true;
};

const pickFirstMeaningfulValue = (...values) => {
  for (const value of values) {
    if (hasMeaningfulValue(value)) {
      return value;
    }
  }

  return "";
};

const normalizeComparableFiles = (files = []) =>
  normalizeUploadedFiles(files)
    .map((file) => String(file || "").trim())
    .filter(Boolean)
    .sort();

const areComparableArraysEqual = (left = [], right = []) => {
  if (left.length !== right.length) return false;
  return left.every((value, index) => value === right[index]);
};

const pickText = (...values) => {
  for (const value of values) {
    const nextValue = String(value ?? "").trim();
    if (nextValue) return nextValue;
  }
  return "";
};

const normalizeNullableText = (value) => {
  const normalizedValue = String(value ?? "").trim();
  return normalizedValue || null;
};

const pickOptionLabelById = (options = [], value) => {
  const normalizedId = Number(value);
  if (!Number.isFinite(normalizedId) || normalizedId <= 0) return "";

  const matchedOption = options.find((option, index) => {
    const optionId = Number(option?.id ?? option?.value ?? index + 1);
    return Number.isFinite(optionId) && optionId === normalizedId;
  });

  return String(matchedOption?.label || "").trim();
};

const toLabelValueOptions = (options = []) =>
  (Array.isArray(options) ? options : []).map((option) => ({
    ...option,
    optionId: option?.value ?? option?.id ?? option?.optionId,
    label: String(option?.label || option?.name || option?.value || "").trim(),
    value: String(option?.label || option?.name || option?.value || "").trim(),
  }));

const findOptionLabelFromOptionsById = (options = [], value) => {
  const normalizedValue = String(value ?? "").trim();
  if (!normalizedValue) return "";

  const matchedOption = options.find(
    (option) =>
      String(option?.optionId ?? option?.id ?? option?.value ?? "").trim() === normalizedValue
  );

  return String(matchedOption?.label || "").trim();
};
const tabOptions = [
  { key: "general_info", label: "General Information" },
  { key: "service_records", label: "Service Records" },
  { key: "assignment_history", label: "Assignment History" },
  { key: "financial_insurance", label: "Financial & Insurance" },
  { key: "operational_data", label: "Operational Data" },
];

const renderFieldLabel = (label, required = true) => (
  <span className="assets_vehicle_field_label">
    <span>{label}</span>
    {required ? <span className="assets_vehicle_field_required">*</span> : null}
  </span>
);

const findOption = (options = [], value = "") =>
  options.find(
    (option) =>
      String(option?.value || "").toLowerCase() === String(value || "").toLowerCase() ||
      String(option?.label || "").toLowerCase() === String(value || "").toLowerCase()
  ) || null;

const findOptionByOptionIdOrValue = (options = [], optionId = "", value = "") => {
  const normalizedOptionId = String(optionId ?? "").trim();
  const normalizedValue = String(value ?? "").trim().toLowerCase();

  if (normalizedOptionId) {
    const matchedById = options.find(
      (option) => String(option?.optionId ?? option?.id ?? "").trim() === normalizedOptionId
    );
    if (matchedById) return matchedById;
  }

  return (
    (Array.isArray(options) ? options : []).find((option) => {
      const optionValue = String(option?.value ?? "").trim().toLowerCase();
      const optionLabel = String(option?.label ?? "").trim().toLowerCase();
      return optionValue === normalizedValue || optionLabel === normalizedValue;
    }) || null
  );
};

const resolveSelectOptionId = (
  options = [],
  value = "",
  { fallbackId = null, initialValue = "" } = {}
) => {
  const matchedOption = findOptionByOptionIdOrValue(options, "", value);
  const matchedId = Number(
    matchedOption?.optionId ?? matchedOption?.id ?? matchedOption?.value
  );

  if (Number.isFinite(matchedId) && matchedId > 0) {
    return matchedId;
  }

  const normalizedValue = String(value || "").trim().toLowerCase();
  const normalizedInitialValue = String(initialValue || "").trim().toLowerCase();

  if (normalizedValue && normalizedValue === normalizedInitialValue) {
    const fallbackNumber = Number(fallbackId);
    return Number.isFinite(fallbackNumber) && fallbackNumber > 0 ? fallbackNumber : null;
  }

  return null;
};

const normalizeAssignStatusValue = (value = "") => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

  if (
    normalized === "in_stock" ||
    normalized === "installed" ||
    normalized === "installed_vehicle" ||
    normalized === "installed_equipment" ||
    normalized === "under_repair"
  ) {
    return "active";
  }

  if (normalized === "decommissioned") {
    return "inactive";
  }

  return normalized || "active";
};

const assignStatusMeta = (status) => {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "inactive") {
    return { label: "Inactive", color: "#ef0000", icon: inactiveStatusIcon };
  }
  return { label: "Active", color: "#299900", icon: activeLoadStatusIcon };
};

const tyreCurrentStatusMeta = (status) => {
  const normalized = normalizeTyreStatusValue(status);

  if (normalized === "in_stock") {
    return { icon: statusInStockIcon };
  }

  if (normalized === "under_repair") {
    return { icon: statusUnderRepairIcon };
  }

  if (normalized === "inactive") {
    return { icon: inactiveStatusIcon };
  }

  return { icon: statusInstalledIcon };
};

const serviceStatusMeta = (status) => {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "open") return { label: "Open", icon: openIcon, color: "#999999" };
  if (normalized === "pending") return { label: "Pending", icon: pendingIcon, color: "#ff7f02" };
  if (normalized === "closed") return { label: "Closed", icon: closedIcon, color: "#ef0000" };
  if (normalized === "in_progress") return { label: "In progress", icon: inProgressIcon, color: "#4014b8" };
  return { label: "Completed", icon: checkedIcon, color: "#299900" };
};

const formatDateOnly = (value) => {
  if (!value) return "";
  const parsedDate = new Date(value);
  if (!Number.isFinite(parsedDate.getTime())) return "";

  const year = parsedDate.getFullYear();
  const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
  const day = String(parsedDate.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const normalizePositionValue = (value) => {
  const normalized = String(value ?? "").trim();
  if (!normalized) return "";

  const lower = normalized.toLowerCase();
  if (lower === "null" || lower === "custom position") return "";

  return normalized;
};

const hasInstalledTyreStatus = (value = "") => {
  const normalized = normalizeTyreStatusValue(value);
  return (
    normalized === "installed" ||
    normalized === "installed_vehicle" ||
    normalized === "installed_equipment"
  );
};

const formatNumericText = (value) => {
  const parsed = Number(String(value ?? "").replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed.toLocaleString("en-US") : "";
};

const normalizeTyreServiceStatusValue = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_") || "completed";

const mapTyreServiceRows = (services = []) =>
  (Array.isArray(services) ? services : []).map((service) => ({
    id: pickText(service?.service_id, service?.id, "-") || "-",
    workOrderId:
      pickText(
        service?.work_order?.work_order_id,
        service?.work_order?.id,
        service?.work_order_id,
        service?.workOrderId,
        "-"
      ) || "-",
    serviceType:
      pickText(service?.services_type, service?.service_type, service?.serviceType)
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase()) || "-",
    serviceDate:
      formatDateOnly(service?.start_at || service?.complete_at || service?.service_date) || "-",
    odometer: formatNumericText(service?.odo_meter) || "-",
    partCost: toNumber(service?.cost_parts),
    labourCost: toNumber(service?.cost_labor),
    totalCost: toNumber(service?.complete_total || service?.sub_total || service?.total_cost),
    status: normalizeTyreServiceStatusValue(service?.status),
  }));

const mapTyreTripRows = (trips = []) =>
  (Array.isArray(trips) ? trips : []).map((trip) => ({
    tripId: pickText(trip?.trip_id, trip?.id, "-") || "-",
    assignedTo:
      pickText(
        trip?.vehicle?.registration_number,
        trip?.vehicle?.name,
        Number(trip?.vehicle_id) > 0 ? `VH-${trip.vehicle_id}` : "",
        trip?.equipment?.equipment_id,
        trip?.equipment?.serial_number,
        trip?.equipment?.name,
        Number(trip?.equipment_id) > 0 ? `EQP-${trip.equipment_id}` : "",
        trip?.assigned_to,
        trip?.assignedTo,
        trip?.asset_id && trip?.asset_type
          ? `${String(trip.asset_type).toUpperCase()}-${trip.asset_id}`
          : "",
        "-"
      ) || "-",
    distanceValue: toNumber(trip?.covered_distance ?? trip?.distance),
    distanceCovered: formatNumericText(trip?.covered_distance ?? trip?.distance) || "-",
  }));

const mapTyreAssignmentRows = (history = []) =>
  (Array.isArray(history) ? history : []).map((item) => ({
    tyreId:
      pickText(
        item?.tyre_installed_history_id,
        item?.tyre?.tyres_id,
        item?.tyre?.system_id,
        item?.tyre?.id,
        Number(item?.tyre_id) > 0 ? `TY-${item.tyre_id}` : "",
        "-"
      ) || "-",
    assignedTo:
      pickText(
        item?.vehicle?.registration_number,
        item?.vehicle?.name,
        Number(item?.vehicle_id) > 0 ? `VCH-${item.vehicle_id}` : "",
        item?.equipment?.equipment_id,
        item?.equipment?.serial_number,
        item?.equipment?.name,
        Number(item?.equipment_id) > 0 ? `EQP-${item.equipment_id}` : "",
        item?.source_id && item?.source_type
          ? `${String(item.source_type).toUpperCase()}-${item.source_id}`
          : "",
        item?.assigned_to,
        item?.assignedTo,
        item?.asset_id && item?.asset_type
          ? `${String(item.asset_type).toUpperCase()}-${item.asset_id}`
          : "",
        "-"
      ) || "-",
    position:
      pickText(
        item?.position,
        item?.installed_position,
        item?.position_name,
        item?.mount_position,
        item?.location,
        "--"
      ) || "--",
    installedDate:
      formatDateOnly(item?.installed_at || item?.installed_date || item?.created_at) || "-",
    removedDate: formatDateOnly(item?.removed_at || item?.removed_date) || "-",
    startOdometer:
      formatNumericText(
        item?.start_odometer ?? item?.installed_odometer ?? item?.start_odm ?? item?.installed_odm
      ) || "-",
    endOdometer:
      formatNumericText(
        item?.end_odometer ?? item?.removed_odometer ?? item?.end_odm ?? item?.removed_odm
      ) || "-",
    kmDriven: formatNumericText(item?.km_driven ?? item?.distance_driven ?? item?.distance) || "-",
  }));

const ensureCurrentOption = (options = [], value = "") => {
  const normalizedValue = String(value || "").trim();
  if (!normalizedValue) return options;
  if (findOption(options, normalizedValue)) return options;
  return [{ label: normalizedValue, value: normalizedValue }, ...options];
};

const buildTyreGeneralInfoSnapshot = ({
  tyreId,
  values,
  files = [],
  warehouseId,
  brandId,
  modelId,
  tyrePatternId,
  tyreTypeId,
  constructionTypeId,
}) => ({
  serial_number: normalizeComparableValue(values?.serialNumber),
  warehouse_id: Number(warehouseId) || 0,
  brand_id: Number(brandId) || 0,
  model_id: Number(modelId) || 0,
  tyre_pattern_id: Number(tyrePatternId) || 0,
  tyre_type_id: Number(tyreTypeId) || 0,
  construction_type_id: Number(constructionTypeId) || 0,
  size: Number(values?.tyreSize || values?.size) || 0,
  tread_depth: Number(values?.initialTreadDepth) || 0,
  current_tread_depth: Number(values?.currentTreadDepth) || 0,
  tyre_threshold_values: Number(values?.tyreThresholdValues) || 0,
  pressure: Number(values?.recommendedPressure) || 0,
  load_capacity: Number(values?.maximumLoadCapacity) || 0,
  changed_at: Number(values?.changedAt) || 0,
  next_inspection_date: normalizeNullableText(values?.nextInspectionDate),
  last_inspection_date: normalizeNullableText(values?.lastInspectionDate),
  purchase_cost: Number(values?.purchaseCost) || 0,
  purchase_at: normalizeComparableValue(values?.purchaseDate),
  status: resolveApiTyreStatus(values?.currentStatus || values?.status),
  description: String(values?.description || "").trim(),
  notes: String(values?.notes || "").trim(),
  files: normalizeUploadedFiles(files),
  tyre_id: Number(tyreId) || 0,
});

const getUpdatedTyreGeneralInfoKeys = (currentSnapshot = {}, initialSnapshot = {}) =>
  Object.keys(currentSnapshot).filter((key) => {
    if (key === "tyre_id") return false;

    if (key === "files") {
      return !areComparableArraysEqual(
        normalizeComparableFiles(currentSnapshot[key]),
        normalizeComparableFiles(initialSnapshot[key])
      );
    }

    return (
      normalizeComparableValue(currentSnapshot[key]) !==
      normalizeComparableValue(initialSnapshot[key])
    );
  });

const resolveTyreDetailId = (routeValue, stateTyre) => {
  const stateId = Number(stateTyre?.id);
  if (Number.isFinite(stateId) && stateId > 0) return stateId;

  const directRouteId = Number(routeValue);
  if (Number.isFinite(directRouteId) && directRouteId > 0) return directRouteId;

  const digitsMatch = String(routeValue || "").match(/(\d+)$/);
  if (!digitsMatch) return null;

  const derivedId = Number(digitsMatch[1]);
  return Number.isFinite(derivedId) && derivedId > 0 ? derivedId : null;
};

const mapTyreDetailRecord = (row, fallbackTyre = null) => {
  if (!row || typeof row !== "object") return null;

  const fallbackFormData = fallbackTyre?.formData || {};
  const purchaseRecord = Array.isArray(row?.asset_purchases)
    ? row.asset_purchases[0] || {}
    : row?.asset_purchases || {};
  const systemId = pickText(
    row?.tyres_id,
    row?.systemId,
    row?.system_id,
    row?.id,
    fallbackTyre?.systemId,
    fallbackTyre?.id
  );
  if (!systemId) return null;

  const purchaseDate = formatDateOnly(
    pickFirstMeaningfulValue(
      row?.purchase_at,
      row?.purchaseDate,
      purchaseRecord?.purchase_at,
      purchaseRecord?.purchaseDate,
      fallbackFormData?.purchaseDate,
      fallbackTyre?.purchaseDate
    )
  );
  const currentStatus = normalizeTyreStatusValue(
    pickFirstMeaningfulValue(
      row?.currentStatus,
      row?.current_status,
      row?.status,
      fallbackFormData?.currentStatus,
      fallbackFormData?.status,
      fallbackTyre?.currentStatus,
      "inactive"
    )
  );
  const brand = pickText(
    row?.brand?.name,
    row?.brand,
    row?.brand_name,
    fallbackFormData?.brand,
    fallbackTyre?.brand
  );
  const model = pickText(
    row?.model?.name,
    row?.model,
    row?.model_name,
    fallbackFormData?.model,
    fallbackTyre?.model
  );
  const warehouse = pickText(
    row?.warehouse?.name,
    row?.warehouse?.warehouse_name,
    row?.warehouse,
    fallbackFormData?.warehouse,
    fallbackTyre?.warehouse
  );
  const linkedVehicleId = pickText(
    row?.vehicle?.registration_number,
    row?.vehicle?.name,
    Number(row?.vehicle_id) > 0 ? `VH-${row.vehicle_id}` : "",
    fallbackTyre?.linkedVehicleId
  );
  const linkedEquipmentId = pickText(
    row?.equipment?.equipment_id,
    row?.equipment?.serial_number,
    row?.equipment?.name,
    Number(row?.equipment_id) > 0 ? `EQP-${row.equipment_id}` : "",
    fallbackTyre?.linkedEquipmentId
  );
  const totalKmRunValue = toNumber(
    pickFirstMeaningfulValue(
      row?.odm,
      row?.total_km_run,
      row?.totalKmRun,
      row?.totalKmRunValue,
      fallbackFormData?.totalKmRun,
      fallbackTyre?.totalKmRunValue
    ),
    0
  );
  const purchaseCost = toNumber(
    pickFirstMeaningfulValue(
      row?.purchase_cost,
      row?.purchaseCost,
      purchaseRecord?.purchase_cost,
      purchaseRecord?.purchaseCost,
      fallbackFormData?.purchaseCost,
      fallbackTyre?.purchaseCost
    ),
    0
  );
  const size = pickText(
    row?.size,
    row?.tyreSize,
    fallbackFormData?.tyreSize,
    fallbackFormData?.size,
    fallbackTyre?.size
  );
  const tyrePattern = pickText(
    row?.tyre_pattern?.name,
    row?.pattern?.name,
    row?.tyre_pattern_name,
    row?.tyrePattern,
    pickOptionLabelById(tyrePatternOptions, row?.tyre_pattern_id),
    fallbackFormData?.tyrePattern,
    fallbackTyre?.tyrePattern
  );
  const tyreType = pickText(
    row?.tyre_type?.name,
    row?.type?.name,
    row?.tyre_type_name,
    row?.tyreType,
    pickOptionLabelById(tyreTypeOptions, row?.tyre_type_id),
    fallbackFormData?.tyreType,
    fallbackTyre?.tyreType
  );
  const constructionType = pickText(
    row?.construction_type?.name,
    row?.constructionType?.name,
    row?.construction_type_name,
    row?.constructionType,
    pickOptionLabelById(constructionTypeOptions, row?.construction_type_id),
    fallbackFormData?.constructionType,
    fallbackTyre?.constructionType
  );
  const changedAt = pickFirstMeaningfulValue(
    row?.changed_at,
    row?.changedAt,
    fallbackFormData?.changedAt,
    fallbackTyre?.changedAt
  );
  const nextInspectionDate = formatDateOnly(
    pickFirstMeaningfulValue(
      row?.next_inspection_date,
      row?.nextInspectionDate,
      fallbackFormData?.nextInspectionDate,
      fallbackTyre?.nextInspectionDate
    )
  );
  const lastInspectionDate = formatDateOnly(
    pickFirstMeaningfulValue(
      row?.last_inspection_date,
      row?.lastInspectionDate,
      fallbackFormData?.lastInspectionDate,
      fallbackTyre?.lastInspectionDate
    )
  );

  return {
    id: row?.id || fallbackTyre?.id,
    systemId,
    serialNumber: pickText(
      row?.serial_number,
      row?.serialNumber,
      fallbackFormData?.serialNumber,
      fallbackTyre?.serialNumber
    ),
    brand,
    model,
    size,
    tyrePattern,
    tyreType,
    constructionType,
    initialTreadDepth: toNumber(row?.tread_depth ?? row?.initialTreadDepth, 0),
    currentTreadDepth: toNumber(
      pickFirstMeaningfulValue(
        row?.current_tread_depth,
        row?.currentTreadDepth,
        fallbackFormData?.currentTreadDepth,
        fallbackTyre?.currentTreadDepth
      ),
      0
    ),
    tyreThresholdValues: toNumber(
      pickFirstMeaningfulValue(
        row?.tyre_threshold_values,
        row?.tyreThresholdValues,
        fallbackFormData?.tyreThresholdValues,
        fallbackTyre?.tyreThresholdValues
      ),
      0
    ),
    recommendedPressure: toNumber(row?.pressure ?? row?.recommendedPressure, 0),
    maximumLoadCapacity: toNumber(row?.load_capacity ?? row?.maximumLoadCapacity, 0),
    changedAt,
    lastInspectionDate,
    nextInspectionDate,
    purchaseDate,
    purchaseCost,
    warehouse,
    currentStatus,
    linkedVehicleId,
    linkedEquipmentId,
    currentPosition: normalizePositionValue(
      pickFirstMeaningfulValue(
        row?.current_position,
        row?.currentPosition,
        row?.position,
        fallbackFormData?.currentLocation,
        fallbackTyre?.currentPosition
      )
    ),
    installationCount: toNumber(
      pickFirstMeaningfulValue(
        row?.installation_count,
        row?.installationCount,
        fallbackFormData?.installationCount,
        fallbackTyre?.installationCount
      ),
      0
    ),
    installRotationCost: toNumber(
      pickFirstMeaningfulValue(
        row?.install_rotation_cost,
        row?.installRotationCost,
        fallbackFormData?.installRotationCost,
        fallbackTyre?.installRotationCost
      ),
      0
    ),
    repairCost: toNumber(
      pickFirstMeaningfulValue(
        row?.repair_cost,
        row?.repairCost,
        fallbackFormData?.repairCost,
        fallbackTyre?.repairCost
      ),
      0
    ),
    totalKmRunValue,
    totalKmRun: formatKm(totalKmRunValue),
    lastInstalledDate: purchaseDate,
    totalCost: purchaseCost,
    description: pickFirstMeaningfulValue(
      row?.description,
      fallbackFormData?.description,
      fallbackTyre?.description
    ),
    notes: pickFirstMeaningfulValue(row?.notes, fallbackFormData?.notes, fallbackTyre?.notes),
    formData: {
      ...(fallbackTyre?.formData || {}),
      serialNumber: pickText(
        row?.serial_number,
        row?.serialNumber,
        fallbackFormData?.serialNumber,
        fallbackTyre?.serialNumber
      ),
      brand,
      model,
      size,
      tyreSize: size,
      tyrePattern,
      tyreType,
      constructionType,
      brandId: row?.brand?.id ?? row?.brand_id ?? fallbackFormData?.brandId ?? fallbackTyre?.formData?.brandId ?? "",
      modelId: row?.model?.id ?? row?.model_id ?? fallbackFormData?.modelId ?? fallbackTyre?.formData?.modelId ?? "",
      tyrePatternId:
        row?.tyre_pattern?.id ??
        row?.pattern?.id ??
        row?.tyre_pattern_id ??
        fallbackFormData?.tyrePatternId ??
        fallbackTyre?.formData?.tyrePatternId ??
        "",
      tyreTypeId:
        row?.tyre_type?.id ??
        row?.type?.id ??
        row?.tyre_type_id ??
        fallbackFormData?.tyreTypeId ??
        fallbackTyre?.formData?.tyreTypeId ??
        "",
      constructionTypeId:
        row?.construction_type?.id ??
        row?.constructionType?.id ??
        row?.construction_type_id ??
        fallbackFormData?.constructionTypeId ??
        fallbackTyre?.formData?.constructionTypeId ??
        "",
      warehouseId:
        row?.warehouse?.id ??
        row?.warehouse_id ??
        fallbackFormData?.warehouseId ??
        fallbackTyre?.formData?.warehouseId ??
        "",
      initialTreadDepth: toNumber(row?.tread_depth ?? row?.initialTreadDepth, 0),
      currentTreadDepth: toNumber(
        pickFirstMeaningfulValue(
          row?.current_tread_depth,
          row?.currentTreadDepth,
          fallbackFormData?.currentTreadDepth,
          fallbackTyre?.currentTreadDepth
        ),
        0
      ),
      tyreThresholdValues: toNumber(
        pickFirstMeaningfulValue(
          row?.tyre_threshold_values,
          row?.tyreThresholdValues,
          fallbackFormData?.tyreThresholdValues,
          fallbackTyre?.tyreThresholdValues
        ),
        0
      ),
      recommendedPressure: toNumber(row?.pressure ?? row?.recommendedPressure, 0),
      maximumLoadCapacity: toNumber(row?.load_capacity ?? row?.maximumLoadCapacity, 0),
      changedAt,
      lastInspectionDate,
      nextInspectionDate,
      purchaseDate,
      purchaseCost,
      paymentType: pickText(
        purchaseRecord?.payment_type,
        purchaseRecord?.paymentType,
        row?.payment_type,
        row?.paymentType,
        fallbackFormData?.paymentType
      ),
      depreciationMethod: pickText(
        purchaseRecord?.depreciation_method,
        purchaseRecord?.depreciationMethod,
        row?.depreciation_method,
        row?.depreciationMethod,
        fallbackFormData?.depreciationMethod
      ),
      usefulLife: pickFirstMeaningfulValue(
        purchaseRecord?.useful_life,
        purchaseRecord?.usefulLife,
        row?.useful_life,
        row?.usefulLife,
        fallbackFormData?.usefulLife
      ),
      residualValue: pickFirstMeaningfulValue(
        purchaseRecord?.residual_value,
        purchaseRecord?.residualValue,
        row?.residual_value,
        row?.residualValue,
        fallbackFormData?.residualValue
      ),
      warehouse,
      currentStatus,
      currentLocation: normalizePositionValue(
        pickFirstMeaningfulValue(
          row?.current_position,
          row?.currentPosition,
          row?.position,
          fallbackFormData?.currentLocation,
          fallbackTyre?.currentPosition
        )
      ),
      installationCount: pickFirstMeaningfulValue(
        row?.installation_count,
        row?.installationCount,
        fallbackFormData?.installationCount
      ),
      installRotationCost: pickFirstMeaningfulValue(
        row?.install_rotation_cost,
        row?.installRotationCost,
        fallbackFormData?.installRotationCost
      ),
      repairCost: pickFirstMeaningfulValue(
        row?.repair_cost,
        row?.repairCost,
        fallbackFormData?.repairCost
      ),
      status: normalizeTyreStatusValue(
        pickFirstMeaningfulValue(
          fallbackFormData?.currentStatus,
          fallbackFormData?.status,
          fallbackTyre?.formData?.currentStatus,
          fallbackTyre?.formData?.status,
          currentStatus
        )
      ),
      description: pickFirstMeaningfulValue(
        row?.description,
        fallbackFormData?.description,
        fallbackTyre?.description
      ),
      files: Array.isArray(row?.files)
        ? row.files
        : Array.isArray(fallbackFormData?.files)
          ? fallbackFormData.files
          : Array.isArray(fallbackFormData?.attachments)
            ? fallbackFormData.attachments
            : [],
      serviceRecords: Array.isArray(row?.service_records)
        ? row.service_records
        : Array.isArray(fallbackFormData?.serviceRecords)
          ? fallbackFormData.serviceRecords
          : [],
      assignmentHistory: Array.isArray(row?.assignment_history)
        ? row.assignment_history
        : Array.isArray(fallbackFormData?.assignmentHistory)
          ? fallbackFormData.assignmentHistory
          : [],
      operationalHistory: Array.isArray(row?.trip_history)
        ? row.trip_history
        : Array.isArray(fallbackFormData?.operationalHistory)
          ? fallbackFormData.operationalHistory
          : [],
      comments: Array.isArray(row?.comments)
        ? row.comments
        : Array.isArray(fallbackFormData?.comments)
          ? fallbackFormData.comments
          : [],
      activities: Array.isArray(row?.activities)
        ? row.activities
        : Array.isArray(fallbackFormData?.activities)
          ? fallbackFormData.activities
          : [],
      notes: pickFirstMeaningfulValue(
        row?.notes,
        fallbackFormData?.notes,
        fallbackTyre?.notes
      ),
    },
  };
};

const getInitialValues = (tyre, optionSets = {}) => {
  const formData = tyre?.formData || {};
  const resolvedBrand = pickText(
    formData?.brand,
    tyre?.brand,
    findOptionLabelFromOptionsById(optionSets.brandOptions, formData?.brandId)
  );
  const resolvedModel = pickText(
    formData?.model,
    tyre?.model,
    findOptionLabelFromOptionsById(optionSets.modelOptions, formData?.modelId)
  );
  const resolvedPattern = pickText(
    formData?.tyrePattern,
    tyre?.tyrePattern,
    findOptionLabelFromOptionsById(optionSets.patternOptions, formData?.tyrePatternId)
  );
  const resolvedType = pickText(
    formData?.tyreType,
    tyre?.tyreType,
    findOptionLabelFromOptionsById(optionSets.typeOptions, formData?.tyreTypeId)
  );
  const resolvedConstructionType = pickText(
    formData?.constructionType,
    tyre?.constructionType,
    findOptionLabelFromOptionsById(
      optionSets.constructionOptions,
      formData?.constructionTypeId
    )
  );
  const resolvedWarehouse = pickText(
    formData?.warehouse,
    tyre?.warehouse,
    findOptionLabelFromOptionsById(optionSets.warehouseOptions, formData?.warehouseId)
  );

  return {
    systemId: pickFirstMeaningfulValue(tyre?.systemId),
    serialNumber: pickFirstMeaningfulValue(formData?.serialNumber, tyre?.serialNumber),
    brand: resolvedBrand,
    size: pickFirstMeaningfulValue(formData?.size, formData?.tyreSize, tyre?.size),
    model: resolvedModel,
    status: normalizeTyreStatusValue(
      pickFirstMeaningfulValue(
        formData?.currentStatus,
        formData?.status,
        tyre?.currentStatus,
        "in_stock"
      )
    ),
    currentLocation: normalizePositionValue(
      pickFirstMeaningfulValue(formData?.currentLocation, tyre?.currentPosition)
    ),
    totalKmRun: pickFirstMeaningfulValue(formData?.totalKmRun, tyre?.totalKmRunValue),
    installationCount: pickFirstMeaningfulValue(
      formData?.installationCount,
      tyre?.installationCount
    ),
    initialTreadDepth: pickFirstMeaningfulValue(
      formData?.initialTreadDepth,
      tyre?.initialTreadDepth
    ),
    currentTreadDepth: pickFirstMeaningfulValue(
      formData?.currentTreadDepth,
      tyre?.currentTreadDepth
    ),
    tyreThresholdValues: pickFirstMeaningfulValue(
      formData?.tyreThresholdValues,
      tyre?.tyreThresholdValues
    ),
    purchaseCost: pickFirstMeaningfulValue(
      formData?.purchaseCost,
      tyre?.purchaseCost,
      tyre?.totalCost
    ),
    tyrePattern: resolvedPattern,
    maximumLoadCapacity: pickFirstMeaningfulValue(
      formData?.maximumLoadCapacity,
      tyre?.maximumLoadCapacity
    ),
    currentStatus: normalizeTyreStatusValue(
      pickFirstMeaningfulValue(formData?.currentStatus, tyre?.currentStatus, "in_stock")
    ),
    tyreSize: pickFirstMeaningfulValue(formData?.tyreSize, tyre?.size),
    tyreType: resolvedType,
    recommendedPressure: pickFirstMeaningfulValue(
      formData?.recommendedPressure,
      tyre?.recommendedPressure
    ),
    constructionType: resolvedConstructionType,
    purchaseDate: pickFirstMeaningfulValue(formData?.purchaseDate, tyre?.purchaseDate),
    changedAt: pickFirstMeaningfulValue(formData?.changedAt, tyre?.changedAt),
    lastInspectionDate: pickFirstMeaningfulValue(
      formData?.lastInspectionDate,
      tyre?.lastInspectionDate
    ),
    nextInspectionDate: pickFirstMeaningfulValue(
      formData?.nextInspectionDate,
      tyre?.nextInspectionDate
    ),
    paymentType: pickFirstMeaningfulValue(formData?.paymentType),
    depreciationMethod: pickFirstMeaningfulValue(formData?.depreciationMethod),
    usefulLife: pickFirstMeaningfulValue(formData?.usefulLife),
    residualValue: pickFirstMeaningfulValue(formData?.residualValue),
    warehouse: resolvedWarehouse,
    description: pickFirstMeaningfulValue(formData?.description),
    files: Array.isArray(formData?.files)
      ? formData.files
      : Array.isArray(formData?.attachments)
        ? formData.attachments
        : [],
    notes: pickFirstMeaningfulValue(formData?.notes, tyre?.notes),
    installRotationCost: pickFirstMeaningfulValue(
      formData?.installRotationCost,
      tyre?.installRotationCost
    ),
    repairCost: pickFirstMeaningfulValue(formData?.repairCost, tyre?.repairCost),
  };
};

const detailsSchema = Yup.object().shape({
  serialNumber: Yup.string().required("Serial Number is required"),
  brand: Yup.string().required("Brand is required"),
  size: Yup.string().required("Size is required"),
  status: Yup.string().required("Status is required"),
  currentLocation: Yup.string().when("currentStatus", {
    is: hasInstalledTyreStatus,
    then: (schema) => schema.required("Current Location is required"),
    otherwise: (schema) => schema.nullable().notRequired(),
  }),
  totalKmRun: Yup.number().typeError("Total KM Run must be numeric").required("Total KM Run is required"),
  currentStatus: Yup.string().required("Current Status is required"),
  purchaseCost: Yup.number().typeError("Purchase Cost must be numeric").required("Purchase Cost is required"),
  description: Yup.string().test("desc-max", "Description too long", (value) => stripHtml(value).length <= 1000).nullable(),
});

const TyreDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const canUpdate = hasPermission({
    permissionSlug: AssetsPermission.UpdateTyre,
    menuSlug: AssetsSubModules.Assets,
    moduleSlug: AppMainModules.assetsManagement,
  });
  const location = useLocation();
  const { systemId } = useParams();
  const returnPath = getAssetsRegistrationPath(
    getAssetsRegistrationReturnTab(location, "tyres")
  );
  const {
    tyreDetailData,
    tyreDetailLoading,
    tyreDetailError,
    tyreServiceRecordsData,
    tyreServiceRecordsLoading,
    tyreServiceRecordsMeta,
    tyreTripsData,
    tyreTripsLoading,
    tyreTripsMeta,
    tyreAssignmentHistoryData,
    tyreAssignmentHistoryLoading,
    tyreAssignmentHistoryMeta,
    tyreHistoryData,
    tyreHistoryLoading,
    tyreBrandsDropdownData,
    tyreModelsDropdownData,
    tyrePatternsDropdownData,
    tyreTypesDropdownData,
    tyresDropdownData,
  } = useSelector((state) => state.tyre);
  const { vehicleConstructionTypesDropdownData } = useSelector(
    (state) => state.vehicleDropdowns
  );
  const { warehousesDropdownData = [] } = useSelector((state) => state.partVendors);

  const [activeTab, setActiveTab] = useState("general_info");
  const [commentInput, setCommentInput] = useState("");
  const [comments, setComments] = useState([]);
  const [activities, setActivities] = useState([]);
  const [servicePage, setServicePage] = useState(1);
  const [serviceLimit, setServiceLimit] = useState(10);
  const [assignmentPage, setAssignmentPage] = useState(1);
  const [assignmentLimit, setAssignmentLimit] = useState(10);
  const [operationalPage, setOperationalPage] = useState(1);
  const [operationalLimit, setOperationalLimit] = useState(10);

  const currentUser = useMemo(() => getCurrentUser(), []);
  const allTyres = useMemo(() => {
    const source = Array.isArray(tyresDropdownData)
      ? tyresDropdownData
      : findCollectionByKeys(tyresDropdownData, [
        "tyres",
        "tyre",
        "tyresdropdown",
        "tyredropdown",
        "tyrelist",
      ]);

    return (Array.isArray(source) ? source : [])
      .map((row) => mapTyreDetailRecord(row))
      .filter(Boolean);
  }, [tyresDropdownData]);
  const detailTyreId = useMemo(
    () => resolveTyreDetailId(systemId, location?.state?.tyre),
    [location?.state?.tyre, systemId]
  );
  const decodedSystemId = useMemo(
    () => decodeURIComponent(systemId || location?.state?.tyre?.systemId || ""),
    [location?.state?.tyre?.systemId, systemId]
  );
  const mappedTyreDetail = useMemo(
    () => mapTyreDetailRecord(tyreDetailData, location?.state?.tyre),
    [location?.state?.tyre, tyreDetailData]
  );

  useEffect(() => {
    if (!detailTyreId) return;

    dispatch(fetchTyreDetail(detailTyreId));

    return () => {
      dispatch(clearTyreDetailState());
      dispatch(clearTyreHistoryState());
    };
  }, [detailTyreId, dispatch]);

  useEffect(() => {
    dispatch(fetchTyreBrandsDropdown());
    dispatch(fetchTyreModelsDropdown());
    dispatch(fetchTyrePatternsDropdown());
    dispatch(fetchTyreTypesDropdown());
    dispatch(fetchTyresDropdown());
    dispatch(getWarehousesDropdown());
    dispatch(fetchVehicleConstructionTypesDropdown());
  }, [dispatch]);

  const canUseTyreHistoryApi = Number.isFinite(Number(detailTyreId)) && Number(detailTyreId) > 0;
  const canUseTyreServicesApi = canUseTyreHistoryApi;
  const canUseTyreTripsApi = canUseTyreHistoryApi;
  const canUseTyreAssignmentHistoryApi = canUseTyreHistoryApi;

  const handleFetchTyreHistory = useCallback(
    async (historyType = "all") => {
      if (!canUseTyreHistoryApi) return;
      await dispatch(
        fetchTyreHistory({
          history_type: historyType,
          tyre_id: Number(detailTyreId),
        })
      );
    },
    [canUseTyreHistoryApi, detailTyreId, dispatch]
  );

  const handleSubmitTyreComment = useCallback(
    async ({ comment, parent_id, mention_user_ids }) => {
      if (!canUseTyreHistoryApi) return;

      await dispatch(
        commentOnTyre({
          tyre_id: Number(detailTyreId),
          comment,
          parent_id,
          mention_user_ids,
        })
      ).unwrap();
    },
    [canUseTyreHistoryApi, detailTyreId, dispatch]
  );

  useEffect(() => {
    if (!canUseTyreServicesApi) return;

    dispatch(
      fetchTyreServiceRecords({
        tyre_id: Number(detailTyreId),
        page: servicePage,
        limit: serviceLimit,
      })
    );
  }, [canUseTyreServicesApi, detailTyreId, dispatch, serviceLimit, servicePage]);

  useEffect(() => {
    if (!canUseTyreTripsApi) return;

    dispatch(
      fetchTyreTrips({
        tyre_id: Number(detailTyreId),
        page: operationalPage,
        limit: operationalLimit,
      })
    );
  }, [canUseTyreTripsApi, detailTyreId, dispatch, operationalLimit, operationalPage]);

  useEffect(() => {
    if (!canUseTyreAssignmentHistoryApi) return;

    dispatch(
      fetchTyreAssignmentHistory({
        tyre_id: Number(detailTyreId),
      })
    );
  }, [canUseTyreAssignmentHistoryApi, detailTyreId, dispatch]);

  const tyre = useMemo(() => {
    if (mappedTyreDetail) return mappedTyreDetail;
    if (!decodedSystemId) return location?.state?.tyre || null;
    return (
      allTyres.find(
        (row) => String(row?.systemId || "").toLowerCase() === decodedSystemId.toLowerCase()
      ) ||
      location?.state?.tyre ||
      null
    );
  }, [allTyres, decodedSystemId, location?.state?.tyre, mappedTyreDetail]);
  const tyreComments = tyre?.formData?.comments;
  const tyreActivities = tyre?.formData?.activities;

  useEffect(() => {
    setComments(Array.isArray(tyreComments) ? tyreComments : []);
    setActivities(Array.isArray(tyreActivities) ? tyreActivities : []);
    setCommentInput("");
  }, [tyre?.systemId, tyreActivities, tyreComments]);

  const brandBaseOptions = useMemo(
    () =>
      toLabelValueOptions(
        normalizeOptions(tyreBrandsDropdownData, {
          valueKeys: ["id", "brand_id"],
          labelKeys: ["label", "name", "brand_name"],
          defaultLabelPrefix: "Brand",
        })
      ),
    [tyreBrandsDropdownData]
  );
  const modelBaseOptions = useMemo(
    () =>
      toLabelValueOptions(
        normalizeOptions(tyreModelsDropdownData, {
          valueKeys: ["id", "model_id"],
          labelKeys: ["label", "name", "model_name"],
          defaultLabelPrefix: "Model",
        })
      ),
    [tyreModelsDropdownData]
  );
  const patternBaseOptions = useMemo(
    () =>
      toLabelValueOptions(
        normalizeOptions(tyrePatternsDropdownData, {
          valueKeys: ["id", "tyre_pattern_id", "pattern_id"],
          labelKeys: ["label", "name", "pattern_name", "tyre_pattern_name"],
          defaultLabelPrefix: "Pattern",
        })
      ),
    [tyrePatternsDropdownData]
  );
  const typeBaseOptions = useMemo(
    () =>
      toLabelValueOptions(
        normalizeOptions(tyreTypesDropdownData, {
          valueKeys: ["id", "tyre_type_id", "type_id"],
          labelKeys: ["label", "name", "type_name", "tyre_type_name"],
          defaultLabelPrefix: "Type",
        })
      ),
    [tyreTypesDropdownData]
  );
  const constructionBaseOptions = useMemo(() => {
    return toLabelValueOptions(
      normalizeOptions(
        Array.isArray(vehicleConstructionTypesDropdownData)
          ? vehicleConstructionTypesDropdownData
          : findCollectionByKeys(vehicleConstructionTypesDropdownData, [
            "constructiontypes",
            "constructiontype",
            "constructiontypedropdown",
            "constructiontypelist",
            "vehicleconstructiontypes",
          ]),
        {
          valueKeys: ["value", "id", "construction_type_id"],
          labelKeys: ["label", "construction_type_name", "name", "title"],
          defaultLabelPrefix: "Construction Type",
        }
      )
    );
  }, [vehicleConstructionTypesDropdownData]);
  const warehouseBaseOptions = useMemo(
    () =>
      toLabelValueOptions(
        normalizeOptions(warehousesDropdownData, {
          valueKeys: ["value", "id", "warehouse_id"],
          labelKeys: ["label", "warehouse_name", "name"],
          defaultLabelPrefix: "Warehouse",
        })
      ),
    [warehousesDropdownData]
  );

  const initialValues = useMemo(
    () =>
      getInitialValues(tyre, {
        brandOptions: brandBaseOptions,
        modelOptions: modelBaseOptions,
        patternOptions: patternBaseOptions,
        typeOptions: typeBaseOptions,
        constructionOptions: constructionBaseOptions,
        warehouseOptions: warehouseBaseOptions,
      }),
    [
      brandBaseOptions,
      constructionBaseOptions,
      modelBaseOptions,
      patternBaseOptions,
      tyre,
      typeBaseOptions,
      warehouseBaseOptions,
    ]
  );
  const existingGeneralInfoIds = useMemo(
    () => ({
      brandId: Number(tyre?.formData?.brandId) || Number(tyreDetailData?.brand_id) || null,
      modelId: Number(tyre?.formData?.modelId) || Number(tyreDetailData?.model_id) || null,
      tyrePatternId:
        Number(tyre?.formData?.tyrePatternId) || Number(tyreDetailData?.tyre_pattern_id) || null,
      tyreTypeId:
        Number(tyre?.formData?.tyreTypeId) || Number(tyreDetailData?.tyre_type_id) || null,
      constructionTypeId:
        Number(tyre?.formData?.constructionTypeId) ||
        Number(tyreDetailData?.construction_type_id) ||
        null,
      warehouseId:
        Number(tyre?.formData?.warehouseId) || Number(tyreDetailData?.warehouse_id) || null,
    }),
    [
      tyre?.formData?.brandId,
      tyre?.formData?.constructionTypeId,
      tyre?.formData?.modelId,
      tyre?.formData?.tyrePatternId,
      tyre?.formData?.tyreTypeId,
      tyre?.formData?.warehouseId,
      tyreDetailData?.brand_id,
      tyreDetailData?.construction_type_id,
      tyreDetailData?.model_id,
      tyreDetailData?.tyre_pattern_id,
      tyreDetailData?.tyre_type_id,
      tyreDetailData?.warehouse_id,
    ]
  );
  const brandSelectOptions = useMemo(
    () => ensureCurrentOption(brandBaseOptions, initialValues.brand),
    [brandBaseOptions, initialValues.brand]
  );
  const sizeSelectOptions = useMemo(
    () => ensureCurrentOption(tyreSizeOptions, initialValues.size),
    [initialValues.size]
  );
  const tyreSizeSelectOptions = useMemo(
    () => ensureCurrentOption(tyreSizeOptions, initialValues.tyreSize),
    [initialValues.tyreSize]
  );
  const patternSelectOptions = useMemo(
    () => ensureCurrentOption(patternBaseOptions, initialValues.tyrePattern),
    [initialValues.tyrePattern, patternBaseOptions]
  );
  const typeSelectOptions = useMemo(
    () => ensureCurrentOption(typeBaseOptions, initialValues.tyreType),
    [initialValues.tyreType, typeBaseOptions]
  );
  const modelSelectOptions = useMemo(
    () => ensureCurrentOption(modelBaseOptions, initialValues.model),
    [initialValues.model, modelBaseOptions]
  );
  const constructionSelectOptions = useMemo(
    () => ensureCurrentOption(constructionBaseOptions, initialValues.constructionType),
    [constructionBaseOptions, initialValues.constructionType]
  );
  const warehouseSelectOptions = useMemo(
    () => ensureCurrentOption(warehouseBaseOptions, initialValues.warehouse),
    [initialValues.warehouse, warehouseBaseOptions]
  );
  const paymentTypeSelectOptions = useMemo(
    () => ensureCurrentOption(paymentTypeOptions, initialValues.paymentType),
    [initialValues.paymentType]
  );
  const depreciationMethodSelectOptions = useMemo(
    () => ensureCurrentOption(depreciationMethodOptions, initialValues.depreciationMethod),
    [initialValues.depreciationMethod]
  );
  const locationOptions = useMemo(() => {
    const map = new Map();

    [...allTyres, tyre, { currentPosition: initialValues.currentLocation }].filter(Boolean).forEach((row) => {
      const value = normalizePositionValue(row?.currentPosition);
      if (!value) return;
      if (!map.has(value)) map.set(value, { label: value, value });
    });

    return Array.from(map.values());
  }, [allTyres, initialValues.currentLocation, tyre]);

  const serviceRows = useMemo(() => {
    if (canUseTyreServicesApi) {
      return mapTyreServiceRows(tyreServiceRecordsData);
    }

    const source = tyre?.formData?.serviceRecords;
    return Array.isArray(source) ? source : [];
  }, [canUseTyreServicesApi, tyre?.formData?.serviceRecords, tyreServiceRecordsData]);
  const assignmentRows = useMemo(() => {
    if (canUseTyreAssignmentHistoryApi) {
      return mapTyreAssignmentRows(tyreAssignmentHistoryData);
    }

    const source = tyre?.formData?.assignmentHistory;
    return Array.isArray(source) ? source : [];
  }, [canUseTyreAssignmentHistoryApi, tyre?.formData?.assignmentHistory, tyreAssignmentHistoryData]);
  const operationalRows = useMemo(() => {
    if (canUseTyreTripsApi) {
      return mapTyreTripRows(tyreTripsData);
    }

    const source = tyre?.formData?.operationalHistory;
    return Array.isArray(source) ? source : [];
  }, [canUseTyreTripsApi, tyre?.formData?.operationalHistory, tyreTripsData]);

  const assetHistoryItems = useMemo(
    () => [...activities, ...comments].sort((a, b) => toNumber(b?.createdAtTs, 0) - toNumber(a?.createdAtTs, 0)),
    [activities, comments]
  );
  const paginatedServiceRows = useMemo(() => {
    if (canUseTyreServicesApi) return serviceRows;

    const start = (servicePage - 1) * serviceLimit;
    return serviceRows.slice(start, start + serviceLimit);
  }, [canUseTyreServicesApi, serviceLimit, servicePage, serviceRows]);
  const paginatedAssignmentRows = useMemo(() => {
    const hasServerAssignmentPagination =
      Number(tyreAssignmentHistoryMeta?.totalPages) > 0 ||
      Number(tyreAssignmentHistoryMeta?.totalItems) > assignmentRows.length;

    if (canUseTyreAssignmentHistoryApi && hasServerAssignmentPagination) {
      return assignmentRows;
    }

    const start = (assignmentPage - 1) * assignmentLimit;
    return assignmentRows.slice(start, start + assignmentLimit);
  }, [
    assignmentLimit,
    assignmentPage,
    assignmentRows,
    canUseTyreAssignmentHistoryApi,
    tyreAssignmentHistoryMeta?.totalItems,
    tyreAssignmentHistoryMeta?.totalPages,
  ]);
  const paginatedOperationalRows = useMemo(() => {
    if (canUseTyreTripsApi) return operationalRows;

    const start = (operationalPage - 1) * operationalLimit;
    return operationalRows.slice(start, start + operationalLimit);
  }, [canUseTyreTripsApi, operationalLimit, operationalPage, operationalRows]);
  const totalDistanceCovered = useMemo(
    () =>
      operationalRows.reduce(
        (sum, row) => sum + toNumber(row?.distanceValue ?? row?.distanceCovered, 0),
        0
      ),
    [operationalRows]
  );
  const serviceTotalItems = useMemo(() => {
    if (!canUseTyreServicesApi) return serviceRows.length;

    return Number(tyreServiceRecordsMeta?.totalItems) || serviceRows.length;
  }, [canUseTyreServicesApi, serviceRows.length, tyreServiceRecordsMeta?.totalItems]);
  const serviceTotalPages = useMemo(() => {
    if (!canUseTyreServicesApi) {
      return Math.max(1, Math.ceil(serviceRows.length / Math.max(serviceLimit, 1)));
    }

    return (
      Math.max(1, Number(tyreServiceRecordsMeta?.totalPages)) ||
      Math.max(1, Math.ceil(serviceTotalItems / Math.max(serviceLimit, 1)))
    );
  }, [
    canUseTyreServicesApi,
    serviceLimit,
    serviceRows.length,
    serviceTotalItems,
    tyreServiceRecordsMeta?.totalPages,
  ]);
  const assignmentTotalItems = useMemo(() => {
    if (!canUseTyreAssignmentHistoryApi) return assignmentRows.length;

    return Number(tyreAssignmentHistoryMeta?.totalItems) || assignmentRows.length;
  }, [
    assignmentRows.length,
    canUseTyreAssignmentHistoryApi,
    tyreAssignmentHistoryMeta?.totalItems,
  ]);
  const assignmentTotalPages = useMemo(() => {
    if (!canUseTyreAssignmentHistoryApi) {
      return Math.max(1, Math.ceil(assignmentRows.length / Math.max(assignmentLimit, 1)));
    }

    return (
      Math.max(1, Number(tyreAssignmentHistoryMeta?.totalPages)) ||
      Math.max(1, Math.ceil(assignmentTotalItems / Math.max(assignmentLimit, 1)))
    );
  }, [
    assignmentLimit,
    assignmentRows.length,
    assignmentTotalItems,
    canUseTyreAssignmentHistoryApi,
    tyreAssignmentHistoryMeta?.totalPages,
  ]);
  const operationalTotalItems = useMemo(() => {
    if (!canUseTyreTripsApi) return operationalRows.length;

    return Number(tyreTripsMeta?.totalItems) || Number(tyreTripsMeta?.itemCount) || operationalRows.length;
  }, [canUseTyreTripsApi, operationalRows.length, tyreTripsMeta?.itemCount, tyreTripsMeta?.totalItems]);
  const operationalTotalPages = useMemo(() => {
    if (!canUseTyreTripsApi) {
      return Math.max(1, Math.ceil(operationalRows.length / Math.max(operationalLimit, 1)));
    }

    return (
      Math.max(1, Number(tyreTripsMeta?.totalPages)) ||
      Math.max(1, Math.ceil(operationalTotalItems / Math.max(operationalLimit, 1)))
    );
  }, [
    canUseTyreTripsApi,
    operationalLimit,
    operationalRows.length,
    operationalTotalItems,
    tyreTripsMeta?.totalPages,
  ]);

  const handleCommentSend = () => {
    const plainText = stripHtml(commentInput);
    if (!plainText) return;
    const createdAtTs = Date.now();
    const createdAt = new Date(createdAtTs).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });
    setComments((previous) => [
      {
        id: `comment-${createdAtTs}`,
        type: "comment",
        user: currentUser?.username || currentUser?.name || "User",
        image: currentUser?.image || currentUser?.profile_image || currentUser?.profileImage || currentUser?.details?.image || "",
        text: commentInput,
        time: createdAt,
        createdAtTs,
      },
      ...previous,
    ]);
    setCommentInput("");
  };

  const serviceColumns = [
    {
      name: "Service Entry ID",
      selector: (row) => <span className="hyperlink" title={row.id}>{row.id}</span>,
      tableColumn: false,
      minWidth: "155px",
    },
    {
      name: "Work Order ID",
      selector: (row) => <span className="hyperlink" title={row.workOrderId}>{row.workOrderId}</span>,
      tableColumn: false,
      minWidth: "145px",
    },
    { name: "Service Type", selector: (row) => row.serviceType, tableColumn: false, minWidth: "130px" },
    { name: "Service Date", selector: (row) => row.serviceDate, tableColumn: false, minWidth: "130px" },
    { name: "Odometer", selector: (row) => row.odometer, tableColumn: false, minWidth: "110px" },
    {
      name: "Part Cost (PKR)",
      selector: (row) => formatAmount(row.partCost),
      tableColumn: false,
      minWidth: "145px",
    },
    {
      name: "Labour Cost (PKR)",
      selector: (row) => formatAmount(row.labourCost),
      tableColumn: false,
      minWidth: "160px",
    },
    {
      name: "Total Cost (PKR)",
      selector: (row) => formatAmount(row.totalCost),
      tableColumn: false,
      minWidth: "145px",
    },
    {
      name: "Status",
      selector: (row) => {
        const meta = serviceStatusMeta(row.status);
        return (
          <span className="asset_vehicle_table_status" style={{ color: meta.color }}>
            {meta.icon}
            {meta.label}
          </span>
        );
      },
      tableColumn: false,
      minWidth: "130px",
    },
  ];

  const assignmentColumns = [
    {
      name: "Tyre History ID",
      selector: (row) => <span className="hyperlink">{row.tyreId}</span>,
      tableColumn: false,
      minWidth: "160px",
    },
    {
      name: "Assigned To",
      selector: (row) => <span className="hyperlink">{row.assignedTo}</span>,
      tableColumn: false,
      minWidth: "145px",
    },
    { name: "Position", selector: (row) => row.position, tableColumn: false, minWidth: "155px" },
    {
      name: "Installed Date",
      selector: (row) => row.installedDate,
      tableColumn: false,
      minWidth: "135px",
    },
    {
      name: "Removed Date",
      selector: (row) => row.removedDate,
      tableColumn: false,
      minWidth: "135px",
    },
    {
      name: "Start Odometer",
      selector: (row) => row.startOdometer,
      tableColumn: false,
      minWidth: "150px",
    },
    {
      name: "End Odometer",
      selector: (row) => row.endOdometer,
      tableColumn: false,
      minWidth: "145px",
    },
    {
      name: "KM Driven",
      selector: (row) => row.kmDriven,
      tableColumn: false,
      minWidth: "130px",
    },
  ];

  const operationalColumns = [
    {
      name: "Trip ID",
      selector: (row) => <span className="hyperlink">{row.tripId}</span>,
      tableColumn: false,
      minWidth: "140px",
    },
    {
      name: "Assigned To",
      selector: (row) => <span className="hyperlink">{row.assignedTo}</span>,
      tableColumn: false,
      minWidth: "140px",
    },
    {
      name: "Distance Covered (km)",
      selector: (row) => formatAmount(row.distanceCovered),
      tableColumn: false,
      minWidth: "180px",
    },
  ];

  if (tyreDetailLoading && !tyre) {
    return <div className="loading_spinner"><Spinner animation="grow" /></div>;
  }

  if (!tyre) {
    return (
      <div className="assets_vehicle_details_page">
        <Breadcrumbs values={["Assets", "Tyres", "Tyre Details"]} />
        <div className="page_container">
          <div className="table_no_records">{tyreDetailError || "Tyre not found"}</div>
        </div>
      </div>
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={detailsSchema}
      enableReinitialize
      onSubmit={async (values, { setSubmitting }) => {
        if (!canUpdate) {
          setSubmitting(false);
          errorNotify("You do not have permission to update tyres.");
          return;
        }

        if (!detailTyreId) {
          setSubmitting(false);
          errorNotify("Tyre ID is missing");
          return;
        }

        const resolvedIds = {
          brandId: resolveSelectOptionId(brandSelectOptions, values.brand, {
            fallbackId: existingGeneralInfoIds.brandId,
            initialValue: initialValues.brand,
          }),
          modelId: resolveSelectOptionId(modelSelectOptions, values.model, {
            fallbackId: existingGeneralInfoIds.modelId,
            initialValue: initialValues.model,
          }),
          tyrePatternId: resolveSelectOptionId(patternSelectOptions, values.tyrePattern, {
            fallbackId: existingGeneralInfoIds.tyrePatternId,
            initialValue: initialValues.tyrePattern,
          }),
          tyreTypeId: resolveSelectOptionId(typeSelectOptions, values.tyreType, {
            fallbackId: existingGeneralInfoIds.tyreTypeId,
            initialValue: initialValues.tyreType,
          }),
          constructionTypeId: resolveSelectOptionId(
            constructionSelectOptions,
            values.constructionType,
            {
              fallbackId: existingGeneralInfoIds.constructionTypeId,
              initialValue: initialValues.constructionType,
            }
          ),
          warehouseId: resolveSelectOptionId(warehouseSelectOptions, values.warehouse, {
            fallbackId: existingGeneralInfoIds.warehouseId,
            initialValue: initialValues.warehouse,
          }),
        };

        const unresolvedSelections = [
          !resolvedIds.brandId ? "Brand" : null,
          !resolvedIds.modelId ? "Model" : null,
          !resolvedIds.tyrePatternId ? "Tyre Pattern" : null,
          !resolvedIds.tyreTypeId ? "Tyre Type" : null,
          !resolvedIds.constructionTypeId ? "Construction Type" : null,
          !resolvedIds.warehouseId ? "Warehouse" : null,
        ].filter(Boolean);

        if (unresolvedSelections.length) {
          setSubmitting(false);
          errorNotify(
            `Please select valid ${unresolvedSelections.join(", ")} values before saving`
          );
          return;
        }

        const initialSnapshot = buildTyreGeneralInfoSnapshot({
          tyreId: detailTyreId,
          values: initialValues,
          files: initialValues.files || [],
          warehouseId: existingGeneralInfoIds.warehouseId,
          brandId: existingGeneralInfoIds.brandId,
          modelId: existingGeneralInfoIds.modelId,
          tyrePatternId: existingGeneralInfoIds.tyrePatternId,
          tyreTypeId: existingGeneralInfoIds.tyreTypeId,
          constructionTypeId: existingGeneralInfoIds.constructionTypeId,
        });
        const currentSnapshot = buildTyreGeneralInfoSnapshot({
          tyreId: detailTyreId,
          values,
          files: values.files || [],
          warehouseId: resolvedIds.warehouseId,
          brandId: resolvedIds.brandId,
          modelId: resolvedIds.modelId,
          tyrePatternId: resolvedIds.tyrePatternId,
          tyreTypeId: resolvedIds.tyreTypeId,
          constructionTypeId: resolvedIds.constructionTypeId,
        });
        const payload = {
          ...currentSnapshot,
          updatedKeys: getUpdatedTyreGeneralInfoKeys(currentSnapshot, initialSnapshot),
        };

        try {
          await dispatch(updateTyreGeneralInfo(payload)).unwrap();
          setSubmitting(false);
          successNotify("Tyre details updated successfully");
          navigate(returnPath);
        } catch (error) {
          setSubmitting(false);
          errorNotify(error || "Failed to update tyre details");
        }
      }}
    >
      {({
        values,
        errors,
        touched,
        handleBlur,
        handleChange,
        handleSubmit,
        setFieldValue,
        isSubmitting,
      }) => {
        const normalizedCurrentAssignStatus = normalizeAssignStatusValue(
          values.currentStatus || values.status
        );
        const currentStatusMeta = assignStatusMeta(normalizedCurrentAssignStatus);
        const purchaseCost = toNumber(
          values.purchaseCost,
          toNumber(pickFirstMeaningfulValue(tyre?.purchaseCost, tyre?.totalCost), 0)
        );
        const installRotationCost = toNumber(
          values.installRotationCost,
          toNumber(
            pickFirstMeaningfulValue(
              tyre?.formData?.installRotationCost,
              tyre?.installRotationCost
            ),
            0
          )
        );
        const repairCost = toNumber(
          values.repairCost,
          toNumber(
            pickFirstMeaningfulValue(tyre?.formData?.repairCost, tyre?.repairCost),
            0
          )
        );
        const totalKmRun = toNumber(
          values.totalKmRun,
          toNumber(pickFirstMeaningfulValue(tyre?.totalKmRunValue), 0)
        );
        const totalLifecycleCost = purchaseCost + installRotationCost + repairCost;
        const avgCostPerKm = totalKmRun > 0 ? totalLifecycleCost / totalKmRun : 0;

        const statusSelectStyles = {
          control: (baseStyles) => ({
            ...baseStyles,
            minHeight: 20,
            border: "none",
            borderColor: "transparent",
            boxShadow: "none",
            background: "transparent",
            "&:hover": { border: "none", borderColor: "transparent" },
          }),
          valueContainer: (baseStyles) => ({ ...baseStyles, padding: 0, paddingLeft: 0 }),
          singleValue: (baseStyles) => ({
            ...baseStyles,
            color: currentStatusMeta.color,
            fontSize: "14px",
            fontWeight: 600,
          }),
          placeholder: (baseStyles) => ({ ...baseStyles, color: "#3d3f5c", fontSize: "14px" }),
          dropdownIndicator: (baseStyles) => ({ ...baseStyles, color: "#3d3f5c", padding: 0 }),
          menu: (baseStyles) => ({
            ...baseStyles,
            left: "-28px",
            minWidth: "calc(100% + 28px)",
            width: "max-content",
          }),
          option: (baseStyles) => ({
            ...baseStyles,
            padding: "10px 12px",
            whiteSpace: "normal",
            wordBreak: "break-word",
          }),
        };
        const tyreCurrentStatusSelectOptions = ensureCurrentOption(
          tyreCurrentStatusOptions,
          values.currentStatus || values.status
        );
        const tyreCurrentStatusTopOptions = tyreCurrentStatusSelectOptions.map((option) => ({
          ...option,
          icon: tyreCurrentStatusMeta(option.value).icon,
        }));
        const updateFiles = (updater) => {
          const nextFiles = typeof updater === "function" ? updater(values.files || []) : updater;
          setFieldValue("files", nextFiles);
        };

        return (
          <div className="assets_vehicle_details_page">
            <Breadcrumbs values={["Assets", "Tyres", "Tyre Details"]} />

            <div className="page_header access_view">
              <h2>Tyre Details</h2>

              <div className="d-flex align-items-center gap-2">
                <Button
                  cancelBtn={() => navigate(returnPath)}
                  submitBtn={handleSubmit}
                  submitText="Save Details"
                  type="button"
                  shouldRow={false}
                  isShowSubmitBtn={canUpdate}
                  loading={isSubmitting}
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="page_container">
              <Row>
                <Col md={8}>
                  <div className="assets_vehicle_header_strip">
                    <h6>
                      Tyre ID: <span>{values.systemId}</span>
                    </h6>

                    <div className="assets_vehicle_top_status">
                      <span>Assign / Re-assign</span>
                      <Select
                        className="assets_vehicle_status_select"
                        label=""
                        placeholder="Select Status"
                        options={tyreCurrentStatusTopOptions}
                        value={findOption(tyreCurrentStatusTopOptions, values.currentStatus)}
                        onChange={(option) => {
                          const nextCurrentStatus = option?.value || "";
                          setFieldValue("currentStatus", nextCurrentStatus);
                          setFieldValue("status", nextCurrentStatus);
                        }}
                        onBlur={handleBlur}
                        isSearch={false}
                        showIcon
                        icon={<img src={currentStatusMeta.icon} alt={currentStatusMeta.label} className="assets_vehicle_status_icon_inline" />}
                        customStyles={statusSelectStyles}
                        required={false}
                      />
                    </div>
                  </div>

                  <div className="assets_vehicle_tabs_wrap">
                    <CustomTabs tabs={tabOptions} activeTab={activeTab} setActiveTab={setActiveTab} />
                  </div>

                  {activeTab === "general_info" && (
                    <>
                      <div className="content_box assets_vehicle_content_box">
                        <h6>Core Details</h6>
                        <p>Main information for this part record.</p>
                        <Row>
                          <Col md={4}>
                            <Input
                              label={renderFieldLabel("Serial Number")}
                              placeholder="Enter Serial Number"
                              name="serialNumber"
                              value={values.serialNumber}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.serialNumber}
                              touched={touched.serialNumber}
                              required={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Select
                              label={renderFieldLabel("Brand")}
                              placeholder="Select Brand"
                              options={brandSelectOptions}
                              value={findOption(brandSelectOptions, values.brand)}
                              onChange={(option) => setFieldValue("brand", option?.value || "")}
                              onBlur={handleBlur}
                              error={errors.brand}
                              touched={touched.brand}
                              isSearch={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Select
                              label={renderFieldLabel("Size")}
                              placeholder="Select Size"
                              options={sizeSelectOptions}
                              value={findOption(sizeSelectOptions, values.size)}
                              onChange={(option) => setFieldValue("size", option?.value || "")}
                              onBlur={handleBlur}
                              error={errors.size}
                              touched={touched.size}
                              isSearch={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Select
                              label={renderFieldLabel("Status")}
                              placeholder="Select Status"
                              options={tyreCurrentStatusSelectOptions}
                              value={findOption(
                                tyreCurrentStatusSelectOptions,
                                values.status || values.currentStatus
                              )}
                              onChange={(option) => {
                                const nextStatus = option?.value || "";
                                setFieldValue("status", nextStatus);
                                setFieldValue("currentStatus", nextStatus);
                              }}
                              onBlur={handleBlur}
                              error={errors.status}
                              touched={touched.status}
                              isSearch={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Select
                              label={renderFieldLabel(
                                "Current Location",
                                hasInstalledTyreStatus(values.currentStatus)
                              )}
                              placeholder="Select Location"
                              options={locationOptions}
                              value={findOption(locationOptions, values.currentLocation)}
                              onChange={(option) =>
                                setFieldValue("currentLocation", option?.value || "")
                              }
                              onBlur={handleBlur}
                              error={errors.currentLocation}
                              touched={touched.currentLocation}
                              isSearch={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Input
                              label={renderFieldLabel("Total KM Run")}
                              placeholder="00"
                              type="number"
                              name="totalKmRun"
                              value={values.totalKmRun}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.totalKmRun}
                              touched={touched.totalKmRun}
                              showExtraText
                              extraText="km"
                              required={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Input
                              label={renderFieldLabel("Installation Count")}
                              placeholder="00"
                              type="number"
                              name="installationCount"
                              value={values.installationCount}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              required={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Input
                              label={renderFieldLabel("Initial Tread Depth")}
                              placeholder="00"
                              type="number"
                              name="initialTreadDepth"
                              value={values.initialTreadDepth}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              showExtraText
                              extraText="mm"
                              required={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Input
                              label={renderFieldLabel("Current Tread Depth", false)}
                              placeholder="00"
                              type="number"
                              name="currentTreadDepth"
                              value={values.currentTreadDepth}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              showExtraText
                              extraText="mm"
                              required={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Select
                              label={renderFieldLabel("Tyre Pattern")}
                              placeholder="Select Tyre Pattern"
                              options={patternSelectOptions}
                              value={findOption(patternSelectOptions, values.tyrePattern)}
                              onChange={(option) =>
                                setFieldValue("tyrePattern", option?.value || "")
                              }
                              onBlur={handleBlur}
                              isSearch={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Input
                              label={renderFieldLabel("Maximum Load Capacity")}
                              placeholder="00"
                              type="number"
                              name="maximumLoadCapacity"
                              value={values.maximumLoadCapacity}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              showExtraText
                              extraText="kg"
                              required={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Input
                              label={renderFieldLabel("Threshold Values", false)}
                              placeholder="00"
                              type="number"
                              name="tyreThresholdValues"
                              value={values.tyreThresholdValues}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              showExtraText
                              extraText="km"
                              required={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Select
                              label={renderFieldLabel("Current Status")}
                              placeholder="Select Current Status"
                              options={tyreCurrentStatusSelectOptions}
                              value={findOption(tyreCurrentStatusSelectOptions, values.currentStatus)}
                              onChange={(option) => {
                                const nextCurrentStatus = option?.value || "";
                                setFieldValue("currentStatus", nextCurrentStatus);
                                setFieldValue("status", nextCurrentStatus);
                              }}
                              onBlur={handleBlur}
                              error={errors.currentStatus}
                              touched={touched.currentStatus}
                              isSearch={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Select
                              label={renderFieldLabel("Tyre Size")}
                              placeholder="Select Tyre Size"
                              options={tyreSizeSelectOptions}
                              value={findOption(tyreSizeSelectOptions, values.tyreSize)}
                              onChange={(option) => setFieldValue("tyreSize", option?.value || "")}
                              onBlur={handleBlur}
                              isSearch={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Select
                              label={renderFieldLabel("Tyre Type")}
                              placeholder="Select Tyre Type"
                              options={typeSelectOptions}
                              value={findOption(typeSelectOptions, values.tyreType)}
                              onChange={(option) => setFieldValue("tyreType", option?.value || "")}
                              onBlur={handleBlur}
                              isSearch={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Input
                              label={renderFieldLabel("Recommended Pressure")}
                              placeholder="00"
                              type="number"
                              name="recommendedPressure"
                              value={values.recommendedPressure}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              showExtraText
                              extraText="PSI"
                              required={false}
                            />
                          </Col>

                          <Col md={4}>
                            <Select
                              label={renderFieldLabel("Model")}
                              placeholder="Select Model"
                              options={modelSelectOptions}
                              value={findOption(modelSelectOptions, values.model)}
                              onChange={(option) => setFieldValue("model", option?.value || "")}
                              onBlur={handleBlur}
                              isSearch={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Select
                              label={renderFieldLabel("Construction Type")}
                              placeholder="Select Construction Type"
                              options={constructionSelectOptions}
                              value={findOption(constructionSelectOptions, values.constructionType)}
                              onChange={(option) =>
                                setFieldValue("constructionType", option?.value || "")
                              }
                              onBlur={handleBlur}
                              isSearch={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Input
                              label={renderFieldLabel("Changed At", false)}
                              placeholder="00"
                              type="number"
                              name="changedAt"
                              value={values.changedAt}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              showExtraText
                              extraText="km"
                              required={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Input
                              label={renderFieldLabel("Last Inspection Date", false)}
                              placeholder="Select Last Inspection Date"
                              type="date"
                              name="lastInspectionDate"
                              value={values.lastInspectionDate}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              required={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Input
                              label={renderFieldLabel("Next Inspection Date", false)}
                              placeholder="Select Next Inspection Date"
                              type="date"
                              name="nextInspectionDate"
                              value={values.nextInspectionDate}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              required={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Select
                              label={renderFieldLabel("Warehouse")}
                              placeholder="Select Warehouse"
                              options={warehouseSelectOptions}
                              value={findOption(warehouseSelectOptions, values.warehouse)}
                              onChange={(option) => setFieldValue("warehouse", option?.value || "")}
                              onBlur={handleBlur}
                              isSearch={false}
                            />
                          </Col>
                          <Col md={12} style={{ padding: 0 }}>
                            <Input
                              label={renderFieldLabel("Description", false)}
                              placeholder="Write detail of part if any..."
                              name="description"
                              value={values.description}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.description}
                              touched={touched.description}
                              quill
                              required={false}
                              setFieldValue={setFieldValue}
                              className="create_vehicle_description"
                            />
                          </Col>
                        </Row>
                      </div>

                      <div className="content_box assets_vehicle_content_box assets_vehicle_documents_section">
                        <h6>Photos &amp; Documents</h6>
                        <p>Upload relevant photos or documents for this tyre.</p>
                        <FileUpload
                          destination="tyre-registration"
                          files={values.files || []}
                          setFiles={updateFiles}
                          maxFileSize={10}
                          maxFiles={5}
                          className="create_vehicle_upload assets_vehicle_documents_upload"
                        />
                      </div>
                    </>
                  )}

                  {activeTab === "service_records" && (
                    <div className="content_box assets_vehicle_content_box">
                      <h6>Tyre Records</h6>
                      <p>Main information for this part record.</p>

                      <NewTableComponent
                        showAddBtn={false}
                        showFilter={false}
                        columns={serviceColumns}
                        data={paginatedServiceRows}
                        currentPage={servicePage}
                        setCurrentPage={setServicePage}
                        limit={serviceLimit}
                        setLimit={(value) => {
                          setServicePage(1);
                          setServiceLimit(value);
                        }}
                        totalItems={serviceTotalItems}
                        totalPages={serviceTotalPages}
                        showPagination
                        paginationLoading={canUseTyreServicesApi && tyreServiceRecordsLoading}
                        loading={canUseTyreServicesApi && tyreServiceRecordsLoading}
                        searchFilters={{}}
                        setSearchFilters={() => { }}
                        tableClassName="assets_vehicle_inner_table tyre_assignment_history_table"
                        limitOptions={[10, 20, 50, 100]}
                        paginationClassName="inline_pagination"
                      />
                    </div>
                  )}

                  {activeTab === "assignment_history" && (
                    <div className="content_box assets_vehicle_content_box">
                      <h6>Tyre Installation History</h6>
                      <p>Main information for this part record.</p>

                      <NewTableComponent
                        showAddBtn={false}
                        showFilter={false}
                        columns={assignmentColumns}
                        data={paginatedAssignmentRows}
                        currentPage={assignmentPage}
                        setCurrentPage={setAssignmentPage}
                        limit={assignmentLimit}
                        setLimit={(value) => {
                          setAssignmentPage(1);
                          setAssignmentLimit(value);
                        }}
                        totalItems={assignmentTotalItems}
                        totalPages={assignmentTotalPages}
                        showPagination
                        paginationLoading={
                          canUseTyreAssignmentHistoryApi && tyreAssignmentHistoryLoading
                        }
                        loading={canUseTyreAssignmentHistoryApi && tyreAssignmentHistoryLoading}
                        searchFilters={{}}
                        setSearchFilters={() => { }}
                        tableClassName="assets_vehicle_inner_table"
                        limitOptions={[10, 20, 50, 100]}
                        paginationClassName="inline_pagination"
                      />
                    </div>
                  )}

                  {activeTab === "financial_insurance" && (
                    <div className="content_box assets_vehicle_content_box">
                      <h6>Purchase Summary</h6>
                      <p>Main information for this part record.</p>

                      <Row>
                        <Col md={4}>
                          <Input
                            label={renderFieldLabel("Purchase Date")}
                            placeholder="DD/MM/YYYY"
                            type="date"
                            name="purchaseDate"
                            value={values.purchaseDate}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            required={false}
                          />
                        </Col>
                        <Col md={4}>
                          <Input
                            label={renderFieldLabel("Purchase Cost")}
                            placeholder="00"
                            type="number"
                            name="purchaseCost"
                            value={values.purchaseCost}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={errors.purchaseCost}
                            touched={touched.purchaseCost}
                            showExtraText
                            extraText="PKR"
                            required={false}
                          />
                        </Col>
                        <Col md={4}>
                          <Select
                            label={renderFieldLabel("Payment Type")}
                            placeholder="Select Payment Type"
                            options={paymentTypeSelectOptions}
                            value={findOption(paymentTypeSelectOptions, values.paymentType)}
                            onChange={(option) => setFieldValue("paymentType", option?.value || "")}
                            onBlur={handleBlur}
                            isSearch={false}
                          />
                        </Col>
                        <Col md={4}>
                          <Select
                            label={renderFieldLabel("Depreciation Method")}
                            placeholder="Select Depreciation Method"
                            options={depreciationMethodSelectOptions}
                            value={findOption(
                              depreciationMethodSelectOptions,
                              values.depreciationMethod
                            )}
                            onChange={(option) =>
                              setFieldValue("depreciationMethod", option?.value || "")
                            }
                            onBlur={handleBlur}
                            isSearch={false}
                          />
                        </Col>
                        <Col md={4}>
                          <Input
                            label={renderFieldLabel("Useful Life")}
                            placeholder="Enter Useful Life"
                            type="number"
                            name="usefulLife"
                            value={values.usefulLife}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            required={false}
                          />
                        </Col>
                        <Col md={4}>
                          <Input
                            label={renderFieldLabel("Residual Value")}
                            placeholder="00"
                            type="number"
                            name="residualValue"
                            value={values.residualValue}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            showExtraText
                            extraText="PKR"
                            required={false}
                          />
                        </Col>
                      </Row>
                    </div>
                  )}

                  {activeTab === "operational_data" && (
                    <div className="content_box assets_vehicle_content_box">
                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "space-between",
                          gap: "16px",
                          flexWrap: "wrap",
                        }}
                      >
                        <div>
                          <h6>Tyre Trip History</h6>
                          <p>Main information for this part record.</p>
                        </div>

                        <div
                          style={{
                            minWidth: "320px",
                            maxWidth: "350px",
                            width: "100%",
                            borderRadius: "14px",
                            background: "#E4FBE0",
                            padding: "12px 20px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "16px",
                          }}
                        >
                          <span
                            style={{
                              color: "#3d3f5c",
                              fontSize: "14px",
                            }}
                          >
                            Total Distance Covered
                          </span>
                          <strong
                            style={{
                              color: "#3d3f5c",
                              fontSize: "18px",
                              fontWeight: 700,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {formatAmount(totalDistanceCovered)}
                            <span style={{ fontSize: "18px", marginLeft: "6px" }}>km</span>
                          </strong>
                        </div>
                      </div>

                      <NewTableComponent
                        showAddBtn={false}
                        showFilter={false}
                        columns={operationalColumns}
                        data={paginatedOperationalRows}
                        currentPage={operationalPage}
                        setCurrentPage={setOperationalPage}
                        limit={operationalLimit}
                        setLimit={(value) => {
                          setOperationalPage(1);
                          setOperationalLimit(value);
                        }}
                        totalItems={operationalTotalItems}
                        totalPages={operationalTotalPages}
                        showPagination
                        paginationLoading={canUseTyreTripsApi && tyreTripsLoading}
                        loading={canUseTyreTripsApi && tyreTripsLoading}
                        searchFilters={{}}
                        setSearchFilters={() => { }}
                        tableClassName="assets_vehicle_inner_table"
                        limitOptions={[10, 20, 50, 100]}
                        paginationClassName="inline_pagination"
                      />
                    </div>
                  )}
                </Col>

                <Col md={4}>
                  <div className="service_cost_summary_details assets_vehicle_right_sidebar">
                    <div className="service_cost_summary mb-3">
                      <h6>Cost Summary</h6>
                      <p>Financial Breakdown of the Service Entries (PKR)</p>
                      <div className="service_item"><div>Purchase Cost</div><div>{formatAmount(purchaseCost)}</div></div>
                      <div className="service_item"><div>Installation &amp; Rotation Cost</div><div>{formatAmount(installRotationCost)}</div></div>
                      <div className="service_item"><div>Repair Cost</div><div>{formatAmount(repairCost)}</div></div>
                      <div className="service_item"><div>Total KM Run</div><div>{formatKm(totalKmRun)}</div></div>
                      <div className="service_item"><div>Total Lifecycle Cost</div><div>{formatAmount(totalLifecycleCost)}</div></div>
                      <div className="assets_vehicle_avg_cost">Avg Cost / km <span>{formatDecimal(avgCostPerKm)} PKR</span></div>
                    </div>

                    <LogsComments
                      {...(canUseTyreHistoryApi
                        ? {
                          historyItems: Array.isArray(tyreHistoryData) ? tyreHistoryData : [],
                          onFetchHistory: handleFetchTyreHistory,
                          onSubmitComment: handleSubmitTyreComment,
                          loading: tyreHistoryLoading,
                        }
                        : {
                          staticHistoryItems: assetHistoryItems,
                          commentValue: commentInput,
                          onCommentChange: setCommentInput,
                          onSendComment: handleCommentSend,
                        })}
                      editorModules={modules}
                      editorFormats={formats}
                      emptyMessages={{
                        all: "No comments or user activity found",
                        comment: "No comments found",
                        logs: "No user activity found",
                      }}
                    />

                    <div className="service_cost_summary_details rfq_right_card">
                      <div className="parts_general_notes">
                        <h5>General Notes</h5>
                        <p>Add any additional notes or observations here.</p>
                        <ReactQuill
                          placeholder={"write notes here..."}
                          modules={modules}
                          theme="snow"
                          value={values.notes || ""}
                          onChange={(val) => setFieldValue("notes", val)}
                          formats={formats}
                          className="react_quill_input notes_quill_input quill-small-font trip_quill_input"
                        />
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          </div>
        );
      }}
    </Formik>
  );
};

export default TyreDetails;
